import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ArrowLeft, BookOpen, CheckCircle2, Clock, GraduationCap, LogOut, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pl-PL", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function HistoryView({ onBack, onOpen, onNewTopic }) {
  const { user, logout } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/history");
        setItems(data.items);
      } catch {
        setError(true);
        setItems([]);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="w-full border-b border-slate-100 sticky top-0 bg-white/80 backdrop-blur z-10">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="history-back-button"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" /> Wróć
          </button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-[#FFD600] flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
            </div>
            <span className="text-lg font-extrabold tracking-tight text-slate-900 hidden sm:inline">SocialStart</span>
          </div>
          <div className="flex items-center gap-2">
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full border border-slate-200"/>
            )}
            <button onClick={logout} className="p-2 rounded-xl text-slate-500 hover:bg-slate-100" aria-label="Wyloguj">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 ss-fadein" data-testid="history-view">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
          <BookOpen className="w-3.5 h-3.5" /> Twoja nauka
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
          Moje tematy
        </h1>
        <p className="mt-2 text-slate-500">Wszystko, czego się nauczyłeś w SocialStart.</p>

        {items === null && (
          <div className="mt-10 flex justify-center">
            <div className="flex gap-1.5">
              {[0,1,2].map(i => (
                <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#FFD600] ss-dot" style={{animationDelay:`${i*0.18}s`}}/>
              ))}
            </div>
          </div>
        )}

        {items && items.length === 0 && !error && (
          <div className="mt-10 bg-slate-50 border border-slate-200 rounded-3xl p-8 text-center">
            <Sparkles className="w-8 h-8 text-yellow-500 mx-auto" />
            <div className="mt-3 font-bold text-slate-900">Jeszcze nic tu nie ma</div>
            <p className="text-sm text-slate-500 mt-1">Zacznij od pierwszego tematu — wróci tu automatycznie.</p>
            <button
              onClick={onNewTopic}
              data-testid="history-new-topic-button"
              className="mt-4 inline-flex items-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-5 py-2.5 rounded-2xl"
            >
              Nowy temat
            </button>
          </div>
        )}

        {error && (
          <div className="mt-10 bg-red-50 border border-red-200 rounded-3xl p-6 text-center" data-testid="history-error">
            <div className="font-bold text-slate-900">Nie udało się wczytać historii</div>
            <p className="text-sm text-slate-600 mt-1">Spróbuj odświeżyć stronę za chwilę.</p>
          </div>
        )}

        {items && items.length > 0 && (
          <ul className="mt-8 space-y-3">
            {items.map((it) => {
              const pct = it.total ? Math.round((it.score / it.total) * 100) : null;
              return (
                <li key={it.session_id}>
                  <button
                    onClick={() => onOpen(it.session_id)}
                    data-testid={`history-item-${it.session_id}`}
                    className="w-full text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                        <Clock className="w-3 h-3" /> {fmtDate(it.created_at)} · plan {it.plan_used}
                      </div>
                      <div className="mt-1 text-base sm:text-lg font-bold text-slate-900 truncate">
                        {it.topic_polished || it.topic}
                      </div>
                      {it.short_summary && (
                        <div className="mt-1 text-sm text-slate-500 line-clamp-2">{it.short_summary}</div>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {pct !== null ? (
                        <div className="flex items-center gap-2 bg-[#FEF9C3] border border-yellow-200 rounded-2xl px-3 py-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <div className="text-sm">
                            <b className="text-slate-900">{it.score}/{it.total}</b>
                            <span className="text-slate-500"> · {pct}%</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-3 py-1.5">
                          Nie zrobiono quizu
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
